/**
 * Notification Center model (v109 §7, prototype v108). Two kinds only:
 *   Flag   = department-owned actionable work  → today's `flagged_accounts` rows (assigned_to_role owns it;
 *            status open / escalated / resolved; assigned_to a person; flagged_by; reason; notes)
 *   System = alerts                            → today's `role_notifications` rows (type, severity, message,
 *            account_name, booking_id, target_role; status unread / cleared)
 *
 * Pure: rows in, one combined feed out, newest first, with the filters the page offers (kind, admin scope, search,
 * date range) and the words each row shows. Nothing here talks to the database.
 *
 * Read vs cleared: "opening a notification marks it read; it does not resolve the underlying Flag" (§7). The existing
 * clear route does more than mark read (it cancels linked SMS and suppresses the automation), so this module treats
 * `cleared` as cleared, and "read" as a separate, per-viewer mark the page keeps until a read-receipt column exists.
 */
import { flagIsOverdue } from './timers.js'

export const ROLE_LABELS = { admin: 'Admin', sales: 'Sales', billing: 'Billing', editing: 'Editing', talent_coordinator: 'Talent Coordinator' }

/** Titles for the system notification types the site emits today; anything else is humanized from its code. */
export const TYPE_TITLES = {
  event_day: 'Event day',
  media_uploaded: 'Media uploaded for initial review',
  media_approved: 'Talent media approved',
  media_issue: 'Media issue reported',
  nightly_billing_report: 'Morning billing report',
  addon_purchased: 'Add-on purchased',
  insurance_form_completed: 'Insurance form completed',
  talent_declined_needs_assignment: 'Talent declined, needs assignment',
  location_changed_talent_assigned: 'Location changed',
  date_changed: 'Date changed',
  stripe_dispute: 'Stripe dispute opened',
  undeliverable_email: 'Undeliverable email address'
}

export function humanizeType(type) {
  if (!type) return 'Notification'
  if (TYPE_TITLES[type]) return TYPE_TITLES[type]
  const s = String(type).replace(/[_-]+/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const FLAG_STATUS_LABEL = { open: 'ACTIVE', escalated: 'ESCALATED', resolved: 'RESOLVED' }

const dateOnly = v => (v ? String(v).slice(0, 10) : '')

/**
 * Everyone's bell starts fresh (Traci, 28 Sep 2026): anything created before midnight Mountain on 1 Oct 2026 counts as
 * read for everyone. It stays in the Notification Center; a Flag stays open until it is resolved.
 */
export const FRESH_START = '2026-10-01T06:00:00Z'
/** The viewer's read mark for a row: their own mark, or the fresh start. */
export const readMark = (marks, key, at) => marks.has(key) || beforeFreshStart(at)
export function beforeFreshStart(at) {
  const t = Date.parse(at || '')
  return Number.isFinite(t) && t < Date.parse(FRESH_START)
}

/** A role_notifications row → feed item. `booking` (optional) adds package words; `read` is the viewer's mark. */
export function systemItem(row, { booking = null, read = false } = {}) {
  return {
    id: `system:${row.id}`,
    sourceId: row.id,
    kind: 'system',
    title: humanizeType(row.type),
    message: row.message || '',
    type: row.type,
    severity: row.severity || 'medium',
    accountName: row.account_name || null,
    bookingId: row.booking_id || null,
    packageText: packageText(booking),
    routedTo: row.target_role || null,
    routedLabel: row.target_role ? `routed to ${ROLE_LABELS[row.target_role] || row.target_role}` : null,
    status: row.status === 'cleared' ? 'cleared' : 'unread',
    read: Boolean(read) || row.status === 'cleared',
    at: row.created_at || null,
    to: row.booking_id ? { booking: row.booking_id } : null
  }
}

/**
 * A staff_notifications row (the new console's own notifications, Traci's list) → feed item. Role rows carry
 * routedRoles; personal rows (flags, mentions) are already only this viewer's. Marked read like any system item;
 * there is no Clear (clearing belongs to the old role_notifications automation).
 */
export function staffItem(row, { read = false } = {}) {
  const roles = Array.isArray(row.target_roles) ? row.target_roles : []
  const labels = roles.includes('all') ? 'everyone' : roles.map(r => ROLE_LABELS[r] || r).join(', ')
  return {
    id: `system:${row.id}`,
    sourceId: row.id,
    kind: 'system',
    staff: true,
    title: row.title || humanizeType(row.type),
    message: row.message || '',
    type: row.type,
    severity: row.severity || 'medium',
    accountName: row.account_name || null,
    bookingId: row.booking_id || null,
    packageText: null,
    routedTo: null,
    routedRoles: roles,
    personal: Boolean(row.target_user_id),
    routedLabel: row.target_user_id ? 'for you' : (labels ? `routed to ${labels}` : null),
    status: 'unread',
    read: Boolean(read),
    at: row.created_at || null,
    to: row.booking_id ? { booking: row.booking_id } : null,
    flag_id: row.flag_id || null
  }
}

/** A flagged_accounts row → feed item. `people` maps user ids to names. */
export function flagItem(row, { people = new Map(), booking = null } = {}) {
  const name = id => (id ? people.get(id) || null : null)
  const clientName = name(row.user_id)
  const assignee = name(row.assigned_to)
  const status = ['open', 'escalated', 'resolved'].includes(row.status) ? row.status : 'open'
  return {
    id: `flag:${row.id}`,
    sourceId: row.id,
    kind: 'flag',
    title: row.reason || 'Flag',
    message: status === 'resolved'
      ? `Resolved${row.resolved_at ? ' ' + formatWhen(row.resolved_at) : ''}${row.notes ? ` · ${row.notes}` : ''}`
      : row.notes || (assignee ? `This Flag is assigned to ${assignee} and remains open.` : 'This Flag is open.'),
    severity: row.severity || 'medium',
    flagStatus: status,
    // Open 24+ hours without being resolved (Traci, 25 Sep 2026): shown as OVERDUE; the owner is also notified
    overdue: status !== 'resolved' && flagIsOverdue(row),
    flagStatusLabel: status !== 'resolved' && flagIsOverdue(row) ? 'OVERDUE' : FLAG_STATUS_LABEL[status],
    accountName: clientName,
    clientId: row.user_id || null,
    bookingId: null,
    packageText: packageText(booking),
    ownerRole: row.assigned_to_role || null,
    routedLabel: [assignee ? `assigned to ${assignee}` : null, row.assigned_to_role ? ROLE_LABELS[row.assigned_to_role] || row.assigned_to_role : null].filter(Boolean).join(' · ') || null,
    flaggedBy: name(row.flagged_by),
    read: status === 'resolved',
    at: row.created_at || null,
    resolvedAt: row.resolved_at || null,
    to: row.user_id ? { client: row.user_id } : null
  }
}

/** "8 Hours - Photography" or "6 hours" from a booking row, or null. */
export function packageText(booking) {
  if (!booking) return null
  const title = booking.personalization_data?.package?.packageTitle
  if (title) return String(title)
  const h = Number(booking.hours_booked)
  return Number.isFinite(h) && h > 0 ? `${h} hour${h === 1 ? '' : 's'}` : null
}

export function formatWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' })
}

/**
 * Which rows a role may see (§7): its own department's Flags and its own role's System notifications. Admin sees
 * everything with "Show all", or only Admin-targeted items with "Admin only".
 */
export function visibleTo(items, role, { adminScope = 'all' } = {}) {
  if (role === 'admin' && adminScope === 'all') return items
  return items.filter(it => {
    if (it.personal) return true
    if (it.kind === 'flag') return it.ownerRole === role
    if (Array.isArray(it.routedRoles)) return it.routedRoles.includes('all') || it.routedRoles.includes(role)
    return it.routedTo === role
  })
}

/** The page's filters: kind, free text, date range (YYYY-MM-DD, inclusive), hide-resolved. */
export function filterItems(items, { kind = 'all', search = '', from = '', to = '', hideRead = false } = {}) {
  const q = String(search || '').trim().toLowerCase()
  return items.filter(it => {
    if (kind !== 'all' && it.kind !== kind) return false
    if (hideRead && it.read) return false
    const d = dateOnly(it.at)
    if (from && d && d < from) return false
    if (to && d && d > to) return false
    if (q) {
      const hay = [it.title, it.message, it.accountName, it.bookingId, it.packageText, it.routedLabel, it.type].filter(Boolean).join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}

/** Newest first, by created time. */
export function sortNewest(items) {
  return [...items].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')))
}

export function counts(items) {
  return {
    total: items.length,
    flags: items.filter(i => i.kind === 'flag').length,
    system: items.filter(i => i.kind === 'system').length,
    unread: items.filter(i => !i.read).length,
    openFlags: items.filter(i => i.kind === 'flag' && i.flagStatus !== 'resolved').length
  }
}

/** May this role resolve this Flag? Admin, or anyone in the owning department (§7). */
export function canResolve(item, role) {
  return item.kind === 'flag' && item.flagStatus !== 'resolved' && (role === 'admin' || item.ownerRole === role)
}

/**
 * Per-person read receipts. The `notification_reads` table (user_id, item_kind, item_id) is the record; the browser
 * copy is a fallback for the minutes before the migration has run, and a cache so the page paints read state
 * before the table answers. Item ids are `${kind}:${sourceId}`.
 */
export const READ_KEY = 'lp-notification-center-read'
export function readSet(storage, viewerId) {
  try { return new Set(JSON.parse(storage?.getItem(`${READ_KEY}:${viewerId}`) || '[]')) } catch { return new Set() }
}
export function saveReadSet(storage, viewerId, set) {
  try { storage?.setItem(`${READ_KEY}:${viewerId}`, JSON.stringify([...set].slice(-2000))) } catch { /* private mode */ }
}
/** `${kind}:${id}` → { item_kind, item_id } rows for the table, skipping ids that are not ours. */
export function readRows(viewerId, ids) {
  const out = []
  for (const id of ids) {
    const i = String(id).indexOf(':')
    if (i < 0) continue
    const kind = id.slice(0, i), item = id.slice(i + 1)
    if ((kind === 'system' || kind === 'flag') && item) out.push({ user_id: viewerId, item_kind: kind, item_id: item })
  }
  return out
}
/** Table rows → the `${kind}:${id}` set the page uses. */
export function readIdsFrom(rows) {
  return new Set((rows || []).map(r => `${r.item_kind}:${r.item_id}`))
}
/** Postgres says the table is not there yet (migration not run): fall back to the browser copy. */
export function isMissingTable(error) {
  const code = error?.code, msg = String(error?.message || '')
  return code === '42P01' || code === 'PGRST205' || /notification_reads.*(does not exist|schema cache)/i.test(msg)
}
